/**
 * DPL Jade — Prebuilt Floor Plan Templates
 *
 * Each template is a pure function: () => DCEL state
 * Built by calling the same mutations the user does — no raw state manipulation.
 *
 * Coordinate origin: top-left. All units in mm.
 * Standard grid origin: (500, 500) so there's room to expand in all directions.
 */

import { mkEmpty } from "./dcel";
import { healGraph } from "./heal";
import { mutAddWall, mutSetRoom, mutAddOpening } from "./mutations";
import { OPENING_PRESETS } from "../constants";

const O = 500; // canvas origin offset

// ─── Low-level builder helpers ────────────────────────────────────────────────

/** Add a wall segment; returns updated state */
function wall(st, x1, y1, x2, y2, mat = "BRICK", h = 3000) {
  const thick = { BRICK: 230, AAC: 200, HOLLOW: 200, PARTITION: 100, GLASS: 12, RC: 150 }[mat] || 230;
  return mutAddWall({ x: O + x1, y: O + y1 }, { x: O + x2, y: O + y2 }, "wall", thick, h, mat, st);
}

/** Add walls for a full rectangle */
function rect(st, x, y, w, h, mat = "BRICK", wallH = 3000) {
  st = wall(st, x,   y,   x+w, y,   mat, wallH);
  st = wall(st, x+w, y,   x+w, y+h, mat, wallH);
  st = wall(st, x+w, y+h, x,   y+h, mat, wallH);
  st = wall(st, x,   y+h, x,   y,   mat, wallH);
  return st;
}

/** Assign a room type to the first unassigned face with area > minArea */
function labelRooms(st, assignments) {
  // assignments: [{ type, name, approxCenter: {x,y} }]
  const faces = Object.values(st.faces);
  const used  = new Set();

  for (const { type, name, approxCenter } of assignments) {
    // Find closest face centroid to approxCenter
    let best = null, bestD = Infinity;
    for (const f of faces) {
      if (used.has(f.id)) continue;
      if (!f.centroid) continue;
      const dx = f.centroid.x - (O + approxCenter.x);
      const dy = f.centroid.y - (O + approxCenter.y);
      const d  = dx*dx + dy*dy;
      if (d < bestD) { bestD = d; best = f; }
    }
    if (best) {
      used.add(best.id);
      st = mutSetRoom(best.id, type, name, st);
    }
  }
  return st;
}

/** Final heal pass + project metadata */
function finalise(st, name) {
  const { state } = healGraph(st);
  return { ...state, project: { ...state.project, name } };
}

// ─── Template 1: Studio Apartment (30 m²) ────────────────────────────────────
export function templateStudio() {
  let st = mkEmpty();

  // Outer shell: 6000 × 5000
  st = rect(st, 0, 0, 6000, 5000);

  // Bathroom partition: 1500 × 2000 in corner
  st = wall(st, 0,    3000, 2000, 3000, "PARTITION");
  st = wall(st, 2000, 3000, 2000, 5000, "PARTITION");

  // Kitchen zone: lightweight partition
  st = wall(st, 3500, 0, 3500, 2500, "PARTITION");

  // Door into bathroom
  st = mutAddOpening("dummy", OPENING_PRESETS.DOOR_STD, 0.5, st);

  st = labelRooms(st, [
    { type: "LIVING",   name: "Living / Bedroom", approxCenter: { x: 1750, y: 1500 } },
    { type: "KITCHEN",  name: "Kitchen",          approxCenter: { x: 5000, y: 1000 } },
    { type: "BATHROOM", name: "Bathroom",         approxCenter: { x: 1000, y: 4000 } },
  ]);

  return finalise(st, "Studio Apartment ~30m²");
}

// ─── Template 2: 1 BHK (50 m²) ───────────────────────────────────────────────
export function template1BHK() {
  let st = mkEmpty();

  // Outer shell: 8000 × 6500
  st = rect(st, 0, 0, 8000, 6500);

  // Bedroom: right 3500 × 6500 slice
  st = wall(st, 4500, 0,    4500, 5000);
  // Bathroom off bedroom
  st = wall(st, 4500, 4000, 8000, 4000, "PARTITION");
  // Kitchen partition
  st = wall(st, 0,    4000, 3000, 4000, "PARTITION");
  st = wall(st, 3000, 4000, 3000, 6500, "PARTITION");

  // Windows
  // (openings get placed via the user after template load — topology is enough)

  st = labelRooms(st, [
    { type: "LIVING",   name: "Living Room",  approxCenter: { x: 2000, y: 2000 } },
    { type: "BEDROOM",  name: "Bedroom",      approxCenter: { x: 6000, y: 2000 } },
    { type: "KITCHEN",  name: "Kitchen",      approxCenter: { x: 1200, y: 5200 } },
    { type: "BATHROOM", name: "Bathroom",     approxCenter: { x: 6000, y: 5500 } },
    { type: "TOILET",   name: "Toilet",       approxCenter: { x: 3800, y: 5500 } },
  ]);

  return finalise(st, "1 BHK Apartment ~50m²");
}

// ─── Template 3: 2 BHK (80 m²) ───────────────────────────────────────────────
export function template2BHK() {
  let st = mkEmpty();

  // Outer shell: 12000 × 7000
  st = rect(st, 0, 0, 12000, 7000);

  // Central corridor
  st = wall(st, 0,     3000, 12000, 3000);

  // Bedroom 1: left block 4000 × 3000
  st = wall(st, 4000,  0,    4000,  3000);

  // Bedroom 2: right block 4000 × 3000
  st = wall(st, 8000,  0,    8000,  3000);

  // Living room: centre-bottom 6000 × 4000
  st = wall(st, 4000, 3000,  4000,  7000);
  st = wall(st, 8000, 3000,  8000,  7000);

  // Kitchen bottom-left
  st = wall(st, 0,    5000,  4000,  5000, "PARTITION");

  // Bathrooms
  st = wall(st, 0,    5000,  0,     7000);  // already outer
  st = wall(st, 2000, 5000,  2000,  7000, "PARTITION");

  // Bathroom 2 off bedroom 2
  st = wall(st, 8000, 0,    10000,  0);
  st = wall(st, 10000, 0,   10000,  3000, "PARTITION");

  st = labelRooms(st, [
    { type: "BEDROOM",  name: "Bedroom 1",    approxCenter: { x: 2000, y: 1500 } },
    { type: "BEDROOM",  name: "Bedroom 2",    approxCenter: { x: 6000, y: 1500 } },
    { type: "BEDROOM",  name: "Bedroom 3",    approxCenter: { x: 10000, y: 1500 } },
    { type: "LIVING",   name: "Living Room",  approxCenter: { x: 6000, y: 5200 } },
    { type: "KITCHEN",  name: "Kitchen",      approxCenter: { x: 2000, y: 6000 } },
    { type: "CORRIDOR", name: "Corridor",     approxCenter: { x: 6000, y: 3000 } },
    { type: "BATHROOM", name: "Bathroom 1",   approxCenter: { x: 1000, y: 6000 } },
    { type: "BATHROOM", name: "Bathroom 2",   approxCenter: { x: 9000, y: 1500 } },
  ]);

  return finalise(st, "2 BHK Apartment ~80m²");
}

// ─── Template 4: 3 BHK (110 m²) ──────────────────────────────────────────────
export function template3BHK() {
  let st = mkEmpty();

  // Outer shell: 14000 × 8000
  st = rect(st, 0, 0, 14000, 8000);

  // Horizontal spine corridor at y=3500
  st = wall(st, 0,     3500, 14000, 3500);

  // Bedroom 1: top-left 4500 × 3500
  st = wall(st, 4500,  0,    4500,  3500);

  // Bedroom 2: top-centre 4500 × 3500
  st = wall(st, 9000,  0,    9000,  3500);

  // Bedroom 3: top-right 5000 × 3500
  // (shares outer wall at x=14000)

  // Living + Dining: bottom-right 8000 × 4500
  st = wall(st, 6000, 3500,  6000,  8000);

  // Kitchen bottom-left
  st = wall(st, 0,    6000,  4000,  6000, "PARTITION");
  st = wall(st, 4000, 3500,  4000,  8000, "PARTITION");

  // Bathrooms (top, between bedrooms)
  st = wall(st, 4500,  0,    4500,  1800, "PARTITION"); // already placed
  st = wall(st, 2500,  0,    2500,  3500, "PARTITION");
  st = wall(st, 0,     1800, 2500,  1800, "PARTITION");

  // Master bath (attached to bedroom 3)
  st = wall(st, 11500, 0,   11500,  3500, "PARTITION");
  st = wall(st, 9000,  1800, 11500, 1800, "PARTITION");

  // Utility / store
  st = wall(st, 0,     3500, 0,     6000); // already outer
  st = wall(st, 2500,  3500, 2500,  6000, "PARTITION");

  st = labelRooms(st, [
    { type: "BEDROOM",  name: "Bedroom 1",      approxCenter: { x: 1200, y: 700  } },
    { type: "BATHROOM", name: "Common Bath",     approxCenter: { x: 3500, y: 700  } },
    { type: "BEDROOM",  name: "Bedroom 2",       approxCenter: { x: 6500, y: 1500 } },
    { type: "BEDROOM",  name: "Master Bedroom",  approxCenter: { x: 10000,y: 700  } },
    { type: "BATHROOM", name: "Master Bath",     approxCenter: { x: 10000,y: 2500 } },
    { type: "BEDROOM",  name: "Bedroom 4",       approxCenter: { x: 12500,y: 1500 } },
    { type: "CORRIDOR", name: "Corridor",        approxCenter: { x: 7000, y: 3500 } },
    { type: "LIVING",   name: "Living Room",     approxCenter: { x: 10000,y: 6000 } },
    { type: "KITCHEN",  name: "Kitchen",         approxCenter: { x: 2000, y: 7000 } },
    { type: "STORE",    name: "Utility / Store", approxCenter: { x: 1200, y: 4500 } },
    { type: "LIVING",   name: "Dining Area",     approxCenter: { x: 5000, y: 6000 } },
  ]);

  return finalise(st, "3 BHK Apartment ~110m²");
}

// ─── Template 5: Office layout (60 m²) ───────────────────────────────────────
export function templateOffice() {
  let st = mkEmpty();

  // Outer: 10000 × 6000
  st = rect(st, 0, 0, 10000, 6000);

  // Reception: front 3000 × 6000 slice
  st = wall(st, 3000, 0,    3000, 6000, "PARTITION");

  // Meeting room: back-right 3000 × 3000
  st = wall(st, 7000, 0,    7000, 3500, "GLASS");
  st = wall(st, 3000, 3500, 7000, 3500, "GLASS");

  // Manager cabin: back-left corner 3000 × 2500
  st = wall(st, 3000, 3500, 5500, 3500, "PARTITION");
  st = wall(st, 5500, 3500, 5500, 6000, "PARTITION");

  // Server / pantry room: corner 2000 × 2500
  st = wall(st, 8000, 3500, 8000, 6000, "PARTITION");
  st = wall(st, 7000, 3500, 7000, 6000, "GLASS");

  st = labelRooms(st, [
    { type: "CORRIDOR", name: "Reception",     approxCenter: { x: 1500, y: 3000 } },
    { type: "OFFICE",   name: "Open Office",   approxCenter: { x: 5000, y: 1500 } },
    { type: "OFFICE",   name: "Meeting Room",  approxCenter: { x: 5000, y: 4500 } },
    { type: "OFFICE",   name: "Manager Cabin", approxCenter: { x: 4000, y: 5000 } },
    { type: "STORE",    name: "Server / Pantry",approxCenter:{ x: 9000, y: 5000 } },
  ]);

  return finalise(st, "Small Office ~60m²");
}

// ─── Template 6: Shop / retail unit (40 m²) ──────────────────────────────────
export function templateShop() {
  let st = mkEmpty();

  // Outer: 8000 × 5000
  st = rect(st, 0, 0, 8000, 5000);

  // Store room: rear 8000 × 1500
  st = wall(st, 0, 3500, 8000, 3500, "BRICK");

  // Staff toilet: corner 1500 × 1500
  st = wall(st, 6500, 3500, 6500, 5000, "PARTITION");
  st = wall(st, 6500, 5000, 8000, 5000); // outer

  st = labelRooms(st, [
    { type: "OFFICE",   name: "Shop Floor",    approxCenter: { x: 4000, y: 1500 } },
    { type: "STORE",    name: "Store Room",    approxCenter: { x: 3000, y: 4200 } },
    { type: "TOILET",   name: "Staff Toilet",  approxCenter: { x: 7200, y: 4200 } },
  ]);

  return finalise(st, "Retail / Shop Unit ~40m²");
}

// ─── Template 7: Villa / independent house (150 m²) ──────────────────────────
export function templateVilla() {
  let st = mkEmpty();

  // Ground floor outer shell: 14000 × 11000
  st = rect(st, 0, 0, 14000, 11000, "BRICK", 3300);

  // Entrance / foyer
  st = wall(st, 0,     4000, 5000,  4000, "BRICK", 3300);
  st = wall(st, 5000,  0,    5000,  4000, "BRICK", 3300);

  // Living room: 8000 × 5000
  st = wall(st, 5000,  5000, 14000, 5000, "BRICK", 3300);

  // Dining: 6000 × 4000 center
  st = wall(st, 5000,  4000, 5000,  8000, "BRICK", 3300);
  st = wall(st, 9000,  4000, 9000,  8000, "BRICK", 3300);
  st = wall(st, 5000,  8000, 9000,  8000, "BRICK", 3300);

  // Kitchen: rear left 5000 × 4000
  st = wall(st, 0,     7000, 5000,  7000, "BRICK", 3300);
  st = wall(st, 5000,  7000, 5000, 11000, "BRICK", 3300);

  // Ground floor bedroom
  st = wall(st, 9000,  5000, 9000, 11000, "BRICK", 3300);
  st = wall(st, 9000,  8000, 14000, 8000, "BRICK", 3300);

  // Ground bathroom
  st = wall(st, 11500, 8000, 11500, 11000, "PARTITION");

  // Staircase box
  st = wall(st, 0,    4000, 0,     7000); // outer
  st = wall(st, 2500, 4000, 2500,  7000, "RC");
  st = wall(st, 0,    6000, 2500,  6000, "RC");

  st = labelRooms(st, [
    { type: "CORRIDOR", name: "Foyer",             approxCenter: { x: 2000, y: 2000  } },
    { type: "LIVING",   name: "Living Room",        approxCenter: { x: 7000, y: 2000  } },
    { type: "LIVING",   name: "Dining",             approxCenter: { x: 7000, y: 6000  } },
    { type: "KITCHEN",  name: "Kitchen",            approxCenter: { x: 2000, y: 9000  } },
    { type: "BEDROOM",  name: "Ground Bedroom",     approxCenter: { x: 11500, y: 6500 } },
    { type: "BATHROOM", name: "Ground Bath",        approxCenter: { x: 12500, y: 9500 } },
    { type: "STORE",    name: "Staircase",          approxCenter: { x: 1200, y: 5000  } },
    { type: "ROOM",     name: "Utility",            approxCenter: { x: 7000, y: 9500  } },
  ]);

  return finalise(st, "Villa Ground Floor ~150m²");
}

// ─── Template 8: Hospital ward / clinic (70 m²) ──────────────────────────────
export function templateClinic() {
  let st = mkEmpty();

  // Outer: 10000 × 7000
  st = rect(st, 0, 0, 10000, 7000, "RC", 3600);

  // Waiting area: front 4000 wide
  st = wall(st, 4000, 0,    4000, 7000, "PARTITION");

  // Doctor consultation rooms: right side, 3 rooms
  st = wall(st, 4000, 2333, 10000, 2333, "PARTITION");
  st = wall(st, 4000, 4666, 10000, 4666, "PARTITION");

  // Dispensary / nurse station
  st = wall(st, 7000, 0,    7000, 2333, "PARTITION");

  // Toilet block
  st = wall(st, 0,    5000, 4000, 5000, "PARTITION");
  st = wall(st, 2000, 5000, 2000, 7000, "PARTITION");

  st = labelRooms(st, [
    { type: "CORRIDOR", name: "Waiting Area",       approxCenter: { x: 2000, y: 2500  } },
    { type: "OFFICE",   name: "Consultation Rm 1",  approxCenter: { x: 5500, y: 1000  } },
    { type: "OFFICE",   name: "Dispensary",         approxCenter: { x: 8500, y: 1000  } },
    { type: "OFFICE",   name: "Consultation Rm 2",  approxCenter: { x: 7000, y: 3500  } },
    { type: "OFFICE",   name: "Consultation Rm 3",  approxCenter: { x: 7000, y: 5800  } },
    { type: "TOILET",   name: "Toilet 1",           approxCenter: { x: 1000, y: 6000  } },
    { type: "TOILET",   name: "Toilet 2",           approxCenter: { x: 3000, y: 6000  } },
  ]);

  return finalise(st, "Clinic / Medical Office ~70m²");
}

// ─── Registry ─────────────────────────────────────────────────────────────────
export const TEMPLATES = [
  {
    id:       "studio",
    name:     "Studio Apartment",
    desc:     "Compact single-room layout with kitchen and bathroom. ~30 m².",
    icon:     "🏠",
    category: "Residential",
    area:     30,
    fn:       templateStudio,
  },
  {
    id:       "1bhk",
    name:     "1 BHK Apartment",
    desc:     "One bedroom, living room, kitchen, and two bathrooms. ~50 m².",
    icon:     "🛏",
    category: "Residential",
    area:     50,
    fn:       template1BHK,
  },
  {
    id:       "2bhk",
    name:     "2 BHK Apartment",
    desc:     "Two bedrooms, living, kitchen, corridor. ~80 m².",
    icon:     "🛏",
    category: "Residential",
    area:     80,
    fn:       template2BHK,
  },
  {
    id:       "3bhk",
    name:     "3 BHK Apartment",
    desc:     "Three bedrooms including master suite, living, dining, kitchen. ~110 m².",
    icon:     "🏡",
    category: "Residential",
    area:     110,
    fn:       template3BHK,
  },
  {
    id:       "villa",
    name:     "Villa Ground Floor",
    desc:     "Independent house with foyer, living, dining, kitchen, one bedroom. ~150 m².",
    icon:     "🏘",
    category: "Residential",
    area:     150,
    fn:       templateVilla,
  },
  {
    id:       "office",
    name:     "Small Office",
    desc:     "Reception, open-plan workspace, glass meeting room, manager cabin. ~60 m².",
    icon:     "💼",
    category: "Commercial",
    area:     60,
    fn:       templateOffice,
  },
  {
    id:       "shop",
    name:     "Retail / Shop",
    desc:     "Open shop floor with rear store room and staff toilet. ~40 m².",
    icon:     "🏪",
    category: "Commercial",
    area:     40,
    fn:       templateShop,
  },
  {
    id:       "clinic",
    name:     "Clinic / Medical Office",
    desc:     "Waiting area, three consultation rooms, dispensary, toilets. ~70 m².",
    icon:     "🏥",
    category: "Commercial",
    area:     70,
    fn:       templateClinic,
  },
];
